import { useMemo, useRef } from "react";
import {
  Avatar,
  Popover,
  shortTime,
  previewLabel,
  attachmentType,
  PreviewIcon,
  brandGradient,
  SearchIcon,
  PlusIcon,
  CloseIcon,
  ChevronLeft,
  PinIcon,
  ArchiveIcon,
  TrashIcon,
  BanIcon,
  BellOffIcon,
  UnreadDotIcon,
  DotsIcon,
  ChatGlyph,
  StarIcon,
} from "./ui";
import ScopeSelect from "./ScopeSelect";

const FILTERS = [
  ["all", "All"],
  ["unread", "Unread"],
  ["favorites", "Favourites"],
  ["archived", "Archived"],
];

/** A single row in the conversation options popover — icon sits in a soft tinted
 *  chip, label to the right. `tone` recolours the chip (default / brand / danger). */
function RowMenuItem({ icon: Icon, label, onClick, tone = "default", accent, iconProps }) {
  const chip =
    tone === "danger"
      ? "bg-rose-50 dark:bg-rose-500/15 text-rose-500 group-hover/mi:bg-rose-100 dark:bg-rose-500/15"
      : tone === "brand"
        ? ""
        : "bg-slate-100 text-slate-500 group-hover/mi:bg-slate-200";
  const text = tone === "danger" ? "text-rose-600 dark:text-rose-300" : "text-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group/mi flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left text-[13px] font-semibold transition hover:bg-slate-50 ${text}`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${chip}`}
        style={tone === "brand" && accent ? { backgroundColor: `${accent}18`, color: accent } : undefined}
      >
        <Icon className="h-[18px] w-[18px]" {...iconProps} />
      </span>
      {label}
    </button>
  );
}

/** Small SVG glyph in front of the last-message preview, by attachment type
 *  (handles captioned attachments stored as "<url>\n<caption>"). */
function PreviewGlyph({ body }) {
  return <PreviewIcon kind={attachmentType(body)} className="h-3.5 w-3.5 shrink-0 text-slate-400" />;
}

/**
 * Pane A — the inbox. Owns search, the filter chips, the favourites strip and
 * the conversation rows. Contact picking lives in the same column when
 * `showContacts` is on (screen 2's left half).
 */
export default function InboxPane({
  primary,
  conversations,
  favorites,
  blocked,
  contacts,
  onlineIds,
  archivedCount,
  active,
  filter,
  onFilter,
  showContacts,
  onOpenContacts,
  onCloseContacts,
  contactQuery,
  onContactQuery,
  contactMode = "courses",
  onSelectContactMode,
  contactGroups,
  contactGroup,
  onSelectContactGroup,
  contactCourses = [],
  contactCourse = "",
  onSelectContactCourse,
  previewContact,
  onPreviewContact,
  convoSearch,
  onSearchChange,
  searchResults,
  onOpenSearchResult,
  onOpenConversation,
  onStartChat,
  onConvoAction,
  onRowFavorite,
  onUnblock,
  rowMenu,
  onRowMenu,
  typingIn,
  viewBlocked,
  onViewBlocked,
  embedded,
  onClose,
  searchRef,
}) {
  // Anchor for the row options menu. One ref for the whole list — only one menu
  // is ever open, and the button records itself on click before the menu mounts.
  const rowMenuBtn = useRef(null);

  const list = useMemo(() => {
    if (filter === "unread") return conversations.filter((c) => c.unread > 0);
    if (filter === "favorites") {
      const set = new Set(favorites.map((f) => f.member_token));
      return conversations.filter((c) => set.has(c.member_token));
    }
    return conversations;
  }, [conversations, favorites, filter]);

  const onlineCount = useMemo(
    () => conversations.filter((c) => onlineIds.has(Number(c.other_id))).length,
    [conversations, onlineIds]
  );

  /* ------------------------------------------------------------- header */
  const header = (
    <div className="shrink-0 px-4 pb-2 pt-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-[19px] font-extrabold tracking-tight text-slate-900">
            {showContacts ? "New message" : "Messages"}
          </h2>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
            {showContacts ? (
              contactGroup
                ? `${contacts.length} ${contacts.length === 1 ? "member" : "members"} you can message`
                : "Pick a community to start"
            ) : (
              <>
                {conversations.length} {conversations.length === 1 ? "chat" : "chats"}
                {onlineCount > 0 && (
                  <span className="flex items-center gap-1 text-emerald-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {onlineCount} online
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={showContacts ? onCloseContacts : onOpenContacts}
            aria-label={showContacts ? "Back to messages" : "New message"}
            className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition hover:brightness-110 active:scale-95"
            style={
              showContacts
                ? { backgroundColor: "rgb(var(--c-slate-100))", color: "rgb(var(--c-slate-600))", boxShadow: "none" }
                : { ...brandGradient(primary), boxShadow: `0 8px 20px -6px ${primary}88` }
            }
          >
            {showContacts ? <ChevronLeft className="h-5 w-5" width="2" /> : <PlusIcon className="h-5 w-5" width="2" />}
          </button>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 md:hidden"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  /* ------------------------------------------------- new-message scope */
  const isCourseMode = contactMode === "courses";
  const scopePicked = isCourseMode ? !!contactCourse : !!contactGroup;
  const scopeControls = showContacts && (
    <div className="shrink-0 space-y-2 px-3 pb-2.5" data-testid="chat-scope-picker">
      {/* Courses | Groups toggle + picker — you message members of a space you
          share, exactly as the Members page scopes its list. */}
      <div className="flex items-center gap-2">
        <div className="inline-flex shrink-0 items-center rounded-xl bg-slate-100 p-0.5">
          {[["courses", "Courses"], ["groups", "Communities"]].map(([key, lab]) => {
            const on = contactMode === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelectContactMode && onSelectContactMode(key)}
                className={`rounded-lg px-2.5 py-1.5 text-[12px] font-bold transition ${on ? "bg-surface shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                style={on ? { color: primary } : undefined}
              >
                {lab}
              </button>
            );
          })}
        </div>
        <ScopeSelect
          value={isCourseMode ? contactCourse : contactGroup}
          onChange={isCourseMode ? onSelectContactCourse : onSelectContactGroup}
          options={
            isCourseMode
              ? (contactCourses || []).map((c) => ({ key: String(c.course_page_id), name: c.name, member_count: c.member_count }))
              : (contactGroups || []).map((g) => ({ key: g.group_token, name: g.name, member_count: g.member_count }))
          }
          placeholder={isCourseMode ? "Select course" : "Select community"}
          primary={primary}
        />
      </div>
    </div>
  );

  /* ------------------------------------------------------------- search */
  const searchBox = (
    <div className="shrink-0 px-3 pb-2">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={showContacts ? undefined : searchRef}
          value={showContacts ? contactQuery : convoSearch}
          onChange={(e) => (showContacts ? onContactQuery(e.target.value) : onSearchChange(e.target.value))}
          placeholder={showContacts ? "Search members…" : "Search messages…"}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-16 text-[13px] text-slate-700 transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-surface focus:outline-none focus:ring-0"
        />
        {(showContacts ? contactQuery : convoSearch) ? (
          <button
            type="button"
            onClick={() => (showContacts ? onContactQuery("") : onSearchChange(""))}
            aria-label="Clear"
            className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 text-slate-500 transition hover:bg-slate-300"
          >
            <CloseIcon className="h-3 w-3" width="2.4" />
          </button>
        ) : (
          !showContacts && (
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-surface px-1.5 py-0.5 text-[10px] font-bold text-slate-400 sm:block">
              ⌘K
            </kbd>
          )
        )}
      </div>
    </div>
  );

  /* ------------------------------------------------------------ filters */
  const filterChips = !showContacts && searchResults === null && !viewBlocked && (
    <div className="shrink-0 overflow-x-auto px-3 pb-2.5 scrollbar-hide">
      <div className="flex items-center gap-1.5">
        {FILTERS.map(([key, label]) => {
          const on = filter === key;
          const badge =
            key === "unread"
              ? conversations.reduce((s, c) => s + (c.unread > 0 ? 1 : 0), 0)
              : key === "archived"
                ? archivedCount
                : key === "favorites"
                  ? favorites.length
                  : 0;
          // Hide the Archived chip when there's nothing archived — but never
          // while it's the active filter, or the user gets stranded on it.
          if (key === "archived" && !archivedCount && !on) return null;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onFilter(key)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-bold transition active:scale-95 ${
                on ? "text-white" : "bg-slate-100 text-slate-500 ring-1 ring-slate-200/60 hover:bg-slate-200 hover:text-slate-600"
              }`}
              style={on ? { ...brandGradient(primary), boxShadow: `0 6px 16px -6px ${primary}80` } : undefined}
            >
              {label}
              {badge > 0 && (
                <span
                  className={`flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-extrabold leading-none ${
                    on ? "bg-white/25 text-white" : "bg-surface text-slate-500 shadow-sm"
                  }`}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
        {blocked.length > 0 && (
          <button
            type="button"
            onClick={() => onViewBlocked(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5 text-[12px] font-bold text-slate-500 ring-1 ring-slate-200/60 transition hover:bg-slate-200 hover:text-slate-600 active:scale-95"
          >
            <BanIcon className="h-3.5 w-3.5" /> {blocked.length}
          </button>
        )}
      </div>
    </div>
  );

  /* ---------------------------------------------------------- favourites */
  const favStrip =
    !showContacts && !viewBlocked && searchResults === null && filter === "all" && favorites.length > 0 ? (
      <div className="shrink-0 border-b border-slate-100 px-3 pb-3">
        <p className="mb-2 px-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Favourites</p>
        <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
          {favorites.map((f) => (
            <button
              key={f.member_token}
              type="button"
              onClick={() => onStartChat(f)}
              className="flex w-[54px] shrink-0 flex-col items-center gap-1 transition hover:-translate-y-0.5"
              title={`Chat with ${f.name}`}
            >
              <Avatar name={f.name} img={f.img} primary={primary} size={48} gradientRing online={onlineIds.has(Number(f.other_id))} />
              <span className="w-full truncate text-center text-[10px] font-semibold text-slate-500">{f.name}</span>
            </button>
          ))}
        </div>
      </div>
    ) : null;

  /* ---------------------------------------------------------------- rows */
  const emptyState = (title, hint, cta) => (
    <div className="flex flex-col items-center justify-center gap-2.5 px-6 py-14 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: `${primary}12`, color: primary }}>
        <ChatGlyph className="h-8 w-8" width="1.5" />
      </span>
      <p className="text-[13px] font-bold text-slate-600">{title}</p>
      <p className="max-w-[220px] text-[11px] text-slate-400">{hint}</p>
      {cta}
    </div>
  );

  const body = showContacts ? (
    !scopePicked ? (
      isCourseMode
        ? emptyState(
            "Pick a course",
            "Choose one of your courses above to message the members who have it."
          )
        : emptyState(
            "Pick a community",
            "Choose one of your communities above to message its members."
          )
    ) : contacts.length === 0 ? (
      emptyState("No members found", "No one else here matches — try another name or scope.")
    ) : (
      <ul className="space-y-0.5 px-2 py-1" data-testid="chat-contact-list">
        {contacts.map((c) => {
          const on = previewContact?.member_token === c.member_token;
          return (
            <li key={c.member_token}>
              <button
                type="button"
                onClick={() => onPreviewContact(c)}
                onDoubleClick={() => onStartChat(c)}
                className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition ${on ? "" : "hover:bg-slate-50"}`}
                style={on ? { backgroundColor: `${primary}12` } : undefined}
              >
                <Avatar name={c.name} img={c.img} primary={primary} size={42} ring online={onlineIds.has(Number(c.other_id))} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-bold text-slate-800">{c.name}</span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {onlineIds.has(Number(c.other_id)) ? "Active now" : "Tap to preview"}
                  </span>
                </span>
                <span
                  className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition"
                  style={{ backgroundColor: `${primary}14`, color: primary }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartChat(c);
                  }}
                >
                  Chat
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    )
  ) : viewBlocked ? (
    blocked.length === 0 ? (
      emptyState("No blocked members", "People you block won't be able to message you.")
    ) : (
      <ul className="space-y-0.5 px-2 py-1">
        {blocked.map((b) => (
          <li key={b.member_token} className="flex items-center gap-3 rounded-xl px-2.5 py-2">
            <Avatar name={b.name} img={b.img} primary={primary} size={42} ring />
            <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-slate-700">{b.name}</span>
            <button
              type="button"
              onClick={() => onUnblock(b.member_token)}
              className="shrink-0 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-bold text-slate-600 transition hover:bg-slate-50"
            >
              Unblock
            </button>
          </li>
        ))}
      </ul>
    )
  ) : searchResults !== null ? (
    searchResults.length === 0 ? (
      emptyState("No messages found", "Try another word or a person's name.")
    ) : (
      <ul className="space-y-0.5 px-2 py-1">
        {searchResults.map((r) => (
          <li key={`${r.conversation_id}-${r.message_id}`}>
            <button
              type="button"
              onClick={() => onOpenSearchResult(r)}
              className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition hover:bg-slate-50"
            >
              <Avatar name={r.name} img={r.img} primary={primary} size={42} ring />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13.5px] font-bold text-slate-800">{r.name}</span>
                  <span className="shrink-0 text-[10px] font-semibold text-slate-400">{shortTime(r.created_at)}</span>
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-slate-500">{r.snippet}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    )
  ) : list.length === 0 ? (
    emptyState(
      filter === "unread" ? "Nothing unread" : filter === "archived" ? "No archived chats" : filter === "favorites" ? "No favourites yet" : "No conversations yet",
      filter === "all" ? "Start a chat with anyone in this membership." : "Switch back to All to see every chat.",
      filter === "all" ? (
        <button type="button" onClick={onOpenContacts} className="mt-1 rounded-full px-4 py-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110" style={brandGradient(primary)}>
          Start a chat
        </button>
      ) : null
    )
  ) : (
    <ul className="space-y-0.5 px-2 pb-3 pt-1">
      {list.map((c) => {
        const isActive = active?.conversation_id === c.conversation_id;
        const online = onlineIds.has(Number(c.other_id));
        const typing = typingIn === c.conversation_id;
        return (
          <li key={c.conversation_id} className="relative">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onOpenConversation(c)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onOpenConversation(c);
              }}
              className={`group relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl px-2.5 py-2.5 text-left outline-none transition ${
                isActive ? "" : "hover:bg-slate-50"
              }`}
              style={isActive ? { backgroundColor: `${primary}12` } : undefined}
            >
              {isActive && <span className="absolute left-0 top-1/2 h-8 w-[3px] -translate-y-1/2 rounded-r-full" style={{ backgroundColor: primary }} />}
              <Avatar name={c.name} img={c.img} primary={primary} size={50} ring online={online} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1">
                    {c.pinned ? <PinIcon className="h-3 w-3 shrink-0 text-slate-400" /> : null}
                    <span className={`truncate text-[13.5px] ${c.unread ? "font-extrabold text-slate-900" : "font-bold text-slate-800"}`}>{c.name}</span>
                  </span>
                  <span className={`shrink-0 text-[10px] font-semibold ${c.unread ? "" : "text-slate-400"}`} style={c.unread ? { color: primary } : undefined}>
                    {shortTime(c.last_message_at)}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center justify-between gap-2">
                  {typing ? (
                    <span className="flex items-center gap-1 text-[11.5px] font-bold" style={{ color: primary }}>
                      typing
                      <span className="flex gap-0.5">
                        <span className="h-1 w-1 animate-bounce rounded-full" style={{ backgroundColor: primary, animationDelay: "0ms" }} />
                        <span className="h-1 w-1 animate-bounce rounded-full" style={{ backgroundColor: primary, animationDelay: "150ms" }} />
                        <span className="h-1 w-1 animate-bounce rounded-full" style={{ backgroundColor: primary, animationDelay: "300ms" }} />
                      </span>
                    </span>
                  ) : (
                    <span className={`flex min-w-0 items-center gap-1 truncate text-[11.5px] ${c.unread ? "font-semibold text-slate-700" : "text-slate-400"}`}>
                      {c.muted ? <BellOffIcon className="h-3 w-3 shrink-0" /> : null}
                      <PreviewGlyph body={c.last_message} />
                      <span className="truncate">{c.last_message ? previewLabel(c.last_message) : "No messages yet"}</span>
                    </span>
                  )}
                  {c.unread > 0 && (
                    <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-extrabold text-white shadow-sm" style={brandGradient(primary)}>
                      {c.unread > 9 ? "9+" : c.unread}
                    </span>
                  )}
                </span>
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  rowMenuBtn.current = e.currentTarget;
                  onRowMenu(rowMenu === c.conversation_id ? null : c.conversation_id);
                }}
                aria-label="Conversation options"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 sm:opacity-0 sm:group-hover:opacity-100"
              >
                <DotsIcon className="h-4 w-4" />
              </button>
            </div>

            {rowMenu === c.conversation_id && (() => {
              const isFav = favorites.some((f) => f.member_token === c.member_token);
              return (
                // Portalled: pinned at `top-12` inside the scrolling list, a row
                // near the bottom had its menu cut off by the list's own edge.
                <Popover
                  open
                  onClose={() => onRowMenu(null)}
                  anchorRef={rowMenuBtn}
                  align="end"
                  width={208}
                >
                  <div className="flex items-center gap-2 px-2 pb-1.5 pt-1">
                    <Avatar name={c.name} img={c.img} primary={primary} size={26} />
                    <span className="min-w-0 flex-1 truncate text-[12px] font-extrabold text-slate-800">{c.name}</span>
                  </div>
                  <div className="mb-1 h-px bg-slate-100" />
                  <RowMenuItem
                    icon={StarIcon}
                    iconProps={{ filled: isFav }}
                    tone="brand"
                    accent={isFav ? "#f59e0b" : primary}
                    label={isFav ? "Remove from favourites" : "Add to favourites"}
                    onClick={() => onRowFavorite?.(c)}
                  />
                  <RowMenuItem
                    icon={PinIcon}
                    label={c.pinned ? "Unpin" : "Pin to top"}
                    onClick={() => onConvoAction(c.conversation_id, c.pinned ? "unpin" : "pin")}
                  />
                  <RowMenuItem
                    icon={ArchiveIcon}
                    label={c.archived ? "Unarchive" : "Archive"}
                    onClick={() => onConvoAction(c.conversation_id, c.archived ? "unarchive" : "archive")}
                  />
                  {!c.unread && (
                    <RowMenuItem
                      icon={UnreadDotIcon}
                      tone="brand"
                      accent={primary}
                      iconProps={{ className: "h-3 w-3", style: { color: primary } }}
                      label="Mark as unread"
                      onClick={() => onConvoAction(c.conversation_id, "mark-unread")}
                    />
                  )}
                  <div className="my-1 h-px bg-slate-100" />
                  <RowMenuItem
                    icon={TrashIcon}
                    tone="danger"
                    label="Delete chat"
                    onClick={() => onConvoAction(c.conversation_id, "delete", c.name)}
                  />
                </Popover>
              );
            })()}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {header}
      {scopeControls}
      {/* Search only once there's something to search: always for messages,
          and for the picker only after a course/group is chosen. */}
      {(!showContacts || scopePicked) && searchBox}
      {filterChips}
      {favStrip}
      {viewBlocked && (
        <button type="button" onClick={() => onViewBlocked(false)} className="flex shrink-0 items-center gap-2 border-y border-slate-100 px-4 py-2.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
          <ChevronLeft className="h-4 w-4" /> Blocked members
        </button>
      )}
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto">{body}</div>
    </>
  );
}
